import { pageHref } from "./pagePresentation.js";
import { validateTjtFooter } from "../../theme-library/classic-newspaper/1.0.0/data/tjtFooter.js";
import { buildPublicationUrl } from "./urlBuilder.js";
import { generateArticleSlug } from "../search-discovery/article/generator.js";

export async function loadNavigationTargets(database, publicationId, navigation = {}) {
  const ids = { category: new Set(), page: new Set() };
  const pageSlugs = new Set();
  const collectFooterPages = (items, depth = 0) => { if (depth > 8) return; for (const item of Array.isArray(items) ? items : []) {
    const match = !item?.target && !item?.footerPosition && String(item?.url || "").match(/^\/(?:[a-z0-9-]+\/)?p\/([^/?#]+)\/?$/i);
    if (match) { try { pageSlugs.add(decodeURIComponent(match[1])); } catch { /* Invalid URLs are rejected during validation. */ } }
    collectFooterPages(item?.children, depth + 1);
  } };
  collectFooterPages(navigation.footer);
  const collect = (items, depth = 0) => {
    if (depth > 8) return;
    for (const item of Array.isArray(items) ? items : []) {
      if (item && ids[item.type] && item.target) ids[item.type].add(item.target);
      if (item) collect(item.children, depth + 1);
    }
  };
  collect(navigation.header ?? navigation.main); collect(navigation.footer);
  for (const group of Object.values(navigation.tjtFooter?.groups || {})) collect(group.items);
  const [categories, pages] = await Promise.all([
    ids.category.size ? database.category.findMany({ where: { publicationId, id: { in: [...ids.category] } }, select: { id: true, publicationId: true, slug: true, name: true } }) : [],
    ids.page.size ? database.page.findMany({ where: { publicationId, deletedAt: null, id: { in: [...ids.page] } }, select: { id: true, publicationId: true, slug: true, title: true, externalUrl: true, footerPosition: true, deletedAt: true } }) : [],
  ]);
  if (pageSlugs.size) pages.push(...await database.page.findMany({ where: { publicationId, deletedAt: null, slug: { in: [...pageSlugs] } }, select: { id: true, publicationId: true, slug: true, title: true, footerPosition: true, deletedAt: true } }));
  return { categories: categories.filter(item => item.publicationId === publicationId),
    pages: pages.filter(item => item.publicationId === publicationId && !item.deletedAt) };
}

export function navigationForEditor(navigation = {}, targets = { categories: [], pages: [] }) {
  navigation = navigation && typeof navigation === "object" ? navigation : {};
  const ids = new Set();
  const rows = (items, depth = 0) => (Array.isArray(items) && depth < 9 ? items : []).filter(item => item && typeof item === "object").map((item, order) => {
    const record = targets[item.type === "category" ? "categories" : "pages"]?.find(row => row.id === item.target);
    const urlSlug = String(item.url || "").match(/\/(?:category)\/([^/?#]+)\/?$/)?.[1];
    let slug = item.slug || (item.type === "category" ? record?.slug || urlSlug : "");
    try { slug = decodeURIComponent(slug || ""); } catch { /* Retain invalid text for the editor to correct. */ }
    const id = typeof item.id === "string" && item.id && !ids.has(item.id) ? item.id : crypto.randomUUID();
    ids.add(id);
    return { ...item, footerPosition: item.footerPosition || "", label: typeof item.label === "string" ? item.label : "", type: item.type || (slug ? "category" : "custom_url"), slug: slug || "", target: slug ? "" : item.target || "", url: item.url || (record ? `/p/${record.slug}` : ""), id, order, children: rows(item.children, depth + 1) };
  });
  return { ...navigation, header: rows(navigation.header ?? navigation.main), footer: rows(navigation.footer) };
}

export function newNavigationItem() {
  return { id: crypto.randomUUID(), label: "", slug: "", type: "category", target: "", url: "", children: [] };
}

export function editNavigationLabel(item, label) {
  const automatic = !item.slug || item.slug === generateArticleSlug(item.label);
  return { ...item, label, ...(item.type === "category" && automatic ? { slug: generateArticleSlug(label), target: "", url: "" } : {}) };
}

export function storedNavigationItems(items, settings = {}) {
  const links = (rows, depth = 0) => (Array.isArray(rows) && depth < 9 ? rows : []).filter(item => item && typeof item === "object")
    .map(item => ({ ...item, type: "custom_url", children: links(item.children, depth + 1) }));
  return resolveNavigationItems(links(items), settings);
}

export function resolveNavigationItems(items, settings = {}, categories = [], pages = [], depth = 0) {
  const publication = { settings };
  return (Array.isArray(items) && depth < 9 ? items : []).flatMap(item => {
    if (!item || typeof item !== "object") return [];
    const owned = rows => rows.filter(row => !row.deletedAt && (!settings.publicationId || row.publicationId === settings.publicationId));
    let href = item.url;
    if (item.type === "category") {
      const slug = item.target ? owned(categories).find(row => row.id === item.target)?.slug : item.slug;
      if (!slug) return [];
      href = `/category/${encodeURIComponent(slug)}`;
    } else if (item.type === "page") {
      const page = owned(pages).find(row => row.id === item.target);
      if (item.target && !page) return [];
      if (page) href = pageHref(page, settings);
    }
    if (typeof href !== "string" || !/^(?:\/(?!\/)|https?:\/\/)/i.test(href) || (href.includes("\\") || [...href].some(character => character.charCodeAt(0) <= 32))) return [];
    if (!href.startsWith("/")) { try { new URL(href); } catch { return []; } }
    if (href.startsWith("/")) href = buildPublicationUrl({ publication, path: href });
    const children = resolveNavigationItems(item.children, settings, categories, pages, depth + 1).filter(child => String(child.label).trim().toLowerCase() !== String(item.label).trim().toLowerCase());
    return [{ ...item, href, children }];
  });
}

export async function validateNavigationMenus(database, publication, navigation) {
  const { categories, pages } = await loadNavigationTargets(database, publication.id, navigation);
  let count = 0;
  const ids = new Set();
  const validate = (items, depth = 0) => {
    if (!Array.isArray(items) || depth > 8) throw new Error("Invalid navigation hierarchy.");
    return items.map((item, order) => {
      if (!item || ++count > 200 || !["category", "page", "custom_url"].includes(item.type)) throw new Error("Invalid navigation item.");
      if (typeof item.label !== "string" || !item.label.trim()) throw new Error("Navigation labels are required.");
      if (item.type === "category" && !item.target && (typeof item.slug !== "string" || !/^[\p{L}\p{N}]+(?:[-_][\p{L}\p{N}]+)*$/u.test(item.slug))) throw new Error("Enter a URL-safe category slug using letters, numbers and hyphens.");
      const id = typeof item.id === "string" && item.id && !ids.has(item.id) ? item.id : crypto.randomUUID();
      ids.add(id);
      const children = validate(item.children || [], depth + 1);
      const resolved = resolveNavigationItems([{ ...item, children: [] }], { ...publication.settings, publicationId: publication.id }, categories, pages)[0];
      if (!resolved) throw new Error("Choose a valid category or page belonging to this publication, or a valid URL.");
      if (item.footerPosition && !["col1", "col2", "col3", "col4"].includes(item.footerPosition)) throw new Error("Invalid footer placement.");
      return { id, ...(item.footerPosition ? { footerPosition: item.footerPosition } : {}), label: item.label.trim(), type: item.type, ...(item.type === "category" && !item.target ? { slug: item.slug } : {}), target: item.type === "custom_url" ? "" : item.target || "", url: resolved.href, order, children };
    });
  };
  if (navigation?.footerSectionsSource && !["header", "manual"].includes(navigation.footerSectionsSource)) throw new Error("Invalid footer section source.");
  return { ...(navigation.tjtFooter !== undefined ? { tjtFooter:validateTjtFooter(navigation.tjtFooter, { publicationId:publication.id }, pages, categories) } : {}), header: validate(navigation?.header), footer: validate(navigation?.footer), ...(navigation?.footerSectionsSource ? { footerSectionsSource: navigation.footerSectionsSource } : {}) };
}
